import { collection } from '@/infra/collection'
import { type RoomResource } from '@/features/messaging/api/resources/rooms'
import { Room, type RoomProps } from './Room'
import { Id } from '@/infra/id'
import { type RoomConnectionDescriptor } from './connection-descriptor'
import { filterNonNil } from '@/utils/rx-util'
import { type Observable, concatMap, share } from 'rxjs'
import { type MessageEventResource } from '@/features/messaging/api/resources/messages'
import { type MessagingApi } from '@/features/messaging/api/messaging-api'
import { type RoomEvent } from './room-event'
import { noop } from 'lodash-es'
import type { PrimeMemberByPublicIdFn } from '@/features/iam'
import type { PrimeConversationFn, PrimeRoomFn } from '@/features/messaging'
import {
  mapRoomConnectionDescriptorResourceToProps,
  mapRoomSessionResourceToProps,
  mapRoomSourceResourceToSource,
  mapRoomStatusResourceToStatus,
} from './room-mappers'

/**
 * Consultation rooms.
 */
export class RoomStore {
  /**
   * Room events.
   */
  readonly events$: Observable<RoomEvent>

  /**
   * Collection of rooms.
   */
  readonly rooms = collection({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new Room(),
  })

  constructor(
    private readonly _api: MessagingApi,
    private readonly _primeMemberByPublicId: PrimeMemberByPublicIdFn,
    private readonly _primeConversation: PrimeConversationFn,
  ) {
    this.fetchRoom = this.fetchRoom.bind(this)
    this.joinRoom = this.joinRoom.bind(this)
    this.events$ = this._api.events$.pipe(
      // Ensures events are processed serially. An event whose room fails to
      // load is dropped, so later events still apply.
      concatMap((event) =>
        this._handleServerEvent(event).catch(() => undefined),
      ),
      filterNonNil(),
      share(),
    )

    this.events$.subscribe()
  }

  /**
   * Fetches a Room by id
   */
  async fetchRoom(roomId: Id) {
    const resource = await this._api.rooms.get(Id.assert(roomId))
    return this.rooms.set(this._mapRoomResourceToProps(resource))
  }

  /**
   * Primes a room by ID and fetches it in the background.
   */
  readonly primeRoom: PrimeRoomFn = (props) => {
    if (!props.id) {
      return Room.none
    }

    // The primed room stays as it is when the fetch fails.
    void this.fetchRoom(Id.assert(props.id)).catch(noop)
    return this.rooms.set({ id: props.id })
  }

  /**
   * Gets the connection info to join a room.
   */
  async joinRoom(id: Id): Promise<RoomConnectionDescriptor> {
    return this._api.rooms
      .join(Id.assert(id))
      .then(mapRoomConnectionDescriptorResourceToProps)
  }

  /**
   * Handles realtime events from the server and maps
   * them to application events.
   *
   * @param event
   */
  private async _handleServerEvent(
    event: MessageEventResource,
  ): Promise<RoomEvent> {
    switch (event.type) {
      case 'ROOM_CREATED':
        return {
          type: 'RoomCreated',
          room: await this.fetchRoom(event.payload.id),
        }
      case 'ROOM_CLOSED':
        return {
          type: 'RoomClosed',
          room: await this.fetchRoom(event.payload.id),
        }
      case 'ROOM_PARTICIPANT_CONNECTED':
        return {
          type: 'RoomParticipantConnected',
          room: await this.fetchRoom(event.payload.id),
          participantUserPublicId: event.payload.participant_user_public_id,
        }
      case 'ROOM_PARTICIPANT_DISCONNECTED':
        return {
          type: 'RoomParticipantDisconnected',
          room: await this.fetchRoom(event.payload.id),
          participantUserPublicId: event.payload.participant_user_public_id,
        }
      case 'ROOM_SESSION_STARTED':
        return {
          type: 'RoomSessionStarted',
          room: await this.fetchRoom(event.payload.id),
          sessionId: event.payload.session_id,
        }
      case 'ROOM_SESSION_ENDED':
        return {
          type: 'RoomSessionEnded',
          room: await this.fetchRoom(event.payload.id),
          sessionId: event.payload.session_id,
        }
      default:
        return {
          type: 'Unknown',
          room: Room.none,
        }
    }
  }

  private _mapRoomResourceToProps(resource: RoomResource): RoomProps {
    const conversation = this._primeConversation({
      id: resource.conversation_id,
    })
    return {
      id: resource.id,
      conversation,
      dateCreated: resource.date_created,
      dateModified: resource.date_modified,
      status: mapRoomStatusResourceToStatus(resource.status),
      roomSource: mapRoomSourceResourceToSource(resource.room_source),
      activeParticipants: resource.active_participants.map((userPublicId) =>
        this._primeMemberByPublicId({
          workspaceId: conversation.workspace.id,
          userPublicId,
        }),
      ),
      sessions: resource.sessions.map(mapRoomSessionResourceToProps),
    }
  }
}
